import type { NextPage, GetStaticPropsContext } from 'next'
import { useTranslations } from 'next-intl'
import {
  Box,
  Button,
  Divider,
  Link,
  Text,
  Icon,
  Flex,
  VStack,
  useStyleConfig,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import SeoHead from '@/components/seo-head'
import { Faq } from '@/components/Faq'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { MinerChipIcon } from '@/components/icons/MinerChipIcon'
import { ContactSalesForm } from '@/features/contact-sales/ContactSalesForm'
import { FloatingCallCTA } from '@/features/contact-sales/FloatingCallCTA'
import { SUPPORT_URL } from '@/constants'
import { BuildingIcon } from '@/components/icons/BuildingIcon'
import { QuestionIcon } from '@/components/icons/QuestionIcon'

const ContactSalesPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const gray50 = useColorModeValue('gray.50', 'black.100')
  const t = useTranslations()

  const ReviewSection = () => (
    <Box mt={{ base: '36px', md: '72px' }} px={{ base: '', md: '173px' }}>
      <Text
        textAlign="center"
        fontWeight="400"
        fontSize={{ base: '24px', md: '28px', '2xl': '32px' }}
        lineHeight={{ base: '32px', md: '36px', '2xl': '40px' }}
        variant="brandGradient"
      >
        “Some of the best customer service I&apos;ve experienced in the crypto
        space. I have miners purchased and hosted from a variety of places and
        Compass Mining really makes it easy.”
      </Text>
      <br />
      <Box
        textAlign="center"
        fontSize={{ base: '16px', md: '20px', '2xl': '24px' }}
        lineHeight={{ base: '27px', md: '24px', '2xl': '27px' }}
      >
        <Text fontWeight="bold">- Devin K.</Text>
        <Text>Compass Mining Customer</Text>
      </Box>
    </Box>
  )

  return (
    <>
      <SeoHead
        title={t('contact-sales.seo.title')}
        breadcrumbTitle={t('contact-sales.seo.breadcrumb')}
        description={t('contact-sales.seo.description')}
      />
      <Box __css={styles}>
        <Hero />
        <Box mt={{ base: '14px', md: '150px', '2xl': '100px' }}>
          <ContainerBase
            mt={{ base: '', md: '30px', '2xl': '49px' }}
            maxW={{ xl: '1600px' }}
            padding={{
              base: '32px 16px',
              md: '0 154px',
            }}
          >
            <ReviewSection />
            <Divider
              bg={useColorModeValue('gray.200', 'whiteAlpha.400')}
              mt="100px"
            />
            <Faq
              mt={{ base: '60px', md: '74px' }}
              translationKey="contact-sales"
              questions={[
                'do-you-offer-discounts',
                'when-my-miner-be-online',
                'where-can-i-see-order-status',
                'what-forms-of-payments',
              ]}
            />
          </ContainerBase>
          <Box bg={gray50}>
            <ContainerBase
              mt={{ base: '', md: '200px' }}
              maxW={{ xl: '1600px' }}
              padding={{
                base: '32px 27px',
                md: '103px 154px',
              }}
            >
              <Flex
                alignItems="center"
                gridGap={{ base: '22px', md: '150px' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Box>
                  <Text
                    fontWeight="700"
                    fontSize={{ base: '32px', md: '38px', '2xl': '48px' }}
                    lineHeight={{ base: '34px', md: '38px', '2xl': '48px' }}
                  >
                    {t('contact-sales.looking-for-support')}
                  </Text>
                  <Text
                    fontWeight="500"
                    mt={{ base: '13px', md: '18px' }}
                    fontSize={{ base: '20px', md: '18px', '2xl': '20px' }}
                    lineHeight={{ base: '27px', md: '24px', '2xl': '27px' }}
                  >
                    {t('contact-sales.please-visit-help-section')}
                  </Text>
                </Box>
                <NextLink href={SUPPORT_URL} passHref>
                  <Link
                    variant="nohover"
                    width={{ base: '100%', md: 'initial' }}
                  >
                    <Button
                      variant="black"
                      width={{ base: '100%', xl: '350px' }}
                    >
                      {t('contact-sales.view-help-center')}
                    </Button>
                  </Link>
                </NextLink>
              </Flex>
            </ContainerBase>
          </Box>
        </Box>
      </Box>
      <FloatingCallCTA />
    </>
  )
}

const Hero = () => {
  const t = useTranslations()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const styles = useStyleConfig('Box', { variant: 'white' })
  const sections = [
    {
      key: 'make-enterprise-purchase',
      icon: BuildingIcon,
    },
    {
      key: 'decide-on-miner',
      icon: MinerChipIcon,
    },
    {
      key: 'anything-else',
      icon: QuestionIcon,
    },
  ]

  const ContactForm = () => (
    <Box
      position={{ xl: 'relative' }}
      minW={{ base: '100%', xl: '550px', '2xl': '632px' }}
    >
      <Box
        position={{ xl: 'absolute' }}
        __css={styles}
        height="auto"
        width="100%"
        padding={{
          base: '18px 15px',
          md: '30px 25px',
          '2xl': '50px 43px',
        }}
        borderRadius="4px"
        boxShadow="form"
      >
        <Text
          fontWeight="500"
          fontSize={{ base: '22px', md: '28px', '2xl': '32px' }}
          lineHeight={{ base: '30px', md: '34px' }}
          mb={{ base: '20px', md: '34px', '2xl': '44px' }}
          as="h2"
        >
          {t('contact-sales.mining-questions')}
        </Text>
        <ContactSalesForm />
      </Box>
    </Box>
  )

  return (
    <Box id="mail-section" bg="black.300" position="relative">
      <ContainerBase
        variant="dark"
        bg="black.300"
        maxW={{ xl: '1600px' }}
        p={0}
      >
        <Box
          height="auto"
          minH={{ base: '764px', md: '753px', '2xl': '684px' }}
          padding={{
            base: '52px 16px',
            md: '44px 108px 64px 109px',
            xl: '64px 129px 64px 199px',
          }}
        >
          <Flex
            width="100%"
            justifyContent="space-between"
            flexWrap={{ base: 'wrap', xl: 'unset' }}
            gap="6"
          >
            <Box
              mt={{ md: '20px', xl: '24px' }}
              maxW={{ base: '', md: '', xl: '468px' }}
              textAlign="left"
            >
              <Text
                fontWeight="600"
                fontSize={{ base: '32px', md: '58px', '2xl': '68px' }}
                lineHeight={{ base: '34px', md: '64px', '2xl': '74px' }}
                mb={'16px'}
                variant="brandGradient"
                as="h1"
              >
                {t('contact-sales.hero.title')}
              </Text>
              <Text
                fontWeight="500"
                fontSize={{ base: '16px', md: '18px', '2xl': '20px' }}
                lineHeight={{ base: '22px', md: '27px' }}
              >
                {t('contact-sales.hero.description')}
              </Text>
              <Divider
                display={{ base: 'none', md: 'block' }}
                mt={'16px'}
                mb={'22px'}
              />
              <Flex
                mt={{ base: '42px', md: 0 }}
                flexDirection={{ base: 'column-reverse', md: 'row' }}
              >
                <Box display={{ base: 'none', md: 'block' }}>
                  <Text
                    fontWeight="500"
                    fontSize={{ base: '22px', md: '20px', '2xl': '22px' }}
                    lineHeight={'30px'}
                    mb={{ base: '27px', md: '20px' }}
                    mt={{ base: '24px', md: 0 }}
                  >
                    {t('contact-sales.we-can-help-you')}
                  </Text>
                  <VStack
                    alignItems="flex-start"
                    spacing={{ base: '26px', md: '15px' }}
                    gap="28px"
                  >
                    {sections.map((section) => {
                      return (
                        <Flex
                          key={section.key}
                          alignItems="flex-start"
                          gridGap={{ base: '9px', md: '17px' }}
                          flexDirection={{ base: 'column', md: 'row' }}
                        >
                          <Icon
                            as={section.icon}
                            boxSize="25px"
                            color="brand.600"
                          />
                          <Text fontWeight="500" fontSize="16px">
                            {t(`contact-sales.${section.key}.title`)}
                          </Text>
                        </Flex>
                      )
                    })}
                  </VStack>
                </Box>
                {isMobile && <ContactForm />}
              </Flex>
            </Box>
            {typeof isMobile !== 'undefined' && !isMobile && <ContactForm />}
          </Flex>
        </Box>
      </ContainerBase>
    </Box>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default ContactSalesPage
